<script>
  import ReceiptPageLayout from '../../components/receipt/ReceiptPageLayout.svelte';
  import ReceiptCard from '../../components/receipt/ReceiptCard.svelte';
  import ReceiptHeader from '../../components/receipt/ReceiptHeader.svelte';
  import ReceiptLabel from '../../components/receipt/ReceiptLabel.svelte';
  import ReceiptDivider from '../../components/receipt/ReceiptDivider.svelte';
  import ReceiptItemRow from '../../components/receipt/ReceiptItemRow.svelte';
  import ReceiptSummary from '../../components/receipt/ReceiptSummary.svelte';
  import ReceiptFooter from '../../components/receipt/ReceiptFooter.svelte';
  import ReceiptStamp from '../../components/receipt/ReceiptStamp.svelte';

  let {
    trip = { id: 'trip-001', name: '도쿄 벚꽃 여행', destination: 'TOKYO' },
    items = [
      { id: '1', name: '로이스 초콜릿', category: '식품', quantity: 3, price_cents: 250000, purchased: false },
      { id: '2', name: '유니클로 히트텍', category: '의류', quantity: 2, price_cents: 150000, purchased: false },
      { id: '3', name: '도쿄 바나나', category: '기념품', quantity: 5, price_cents: 80000, purchased: true, purchased_at: '2026-03-22' },
      { id: '4', name: '약용 립밤', category: '뷰티', quantity: 1, price_cents: 45000, purchased: false },
      { id: '5', name: '캔 맥주 세트', category: '식품', quantity: 1, price_cents: 120000, purchased: true, purchased_at: '2026-03-21' },
      { id: '6', name: '마그넷 기념품', category: '기념품', quantity: 4, price_cents: 30000, purchased: false },
    ],
  } = $props();

  let selectedCategory = $state('ALL');

  let categories = $derived(() => {
    const cats = [...new Set(items.map((i) => i.category))];
    return ['ALL', ...cats.sort()];
  });

  let filteredItems = $derived(
    selectedCategory === 'ALL'
      ? items
      : items.filter((i) => i.category === selectedCategory)
  );

  let pendingItems = $derived(filteredItems.filter((i) => !i.purchased));
  let purchasedItems = $derived(filteredItems.filter((i) => i.purchased));

  let totalPendingCents = $derived(
    pendingItems.reduce((sum, i) => sum + i.price_cents * i.quantity, 0)
  );
  let totalPurchasedCents = $derived(
    purchasedItems.reduce((sum, i) => sum + i.price_cents * i.quantity, 0)
  );
  let totalAllCents = $derived(totalPendingCents + totalPurchasedCents);

  let pendingCount = $derived(pendingItems.reduce((sum, i) => sum + i.quantity, 0));
  let purchasedCount = $derived(purchasedItems.reduce((sum, i) => sum + i.quantity, 0));

  let receiptNo = $derived(`SHP-${trip.id.replace(/\D/g, '').slice(-4).padStart(4, '0')}`);

  let timestamp = $derived(new Date().toLocaleString('ko-KR', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }));
</script>

<ReceiptPageLayout>
  {#snippet header()}
    <ReceiptCard>
      <ReceiptHeader
        title="SHOPPING LIST"
        subtitle={trip.destination}
        receiptNo={receiptNo}
      />
    </ReceiptCard>
  {/snippet}

  {#snippet content()}
    <ReceiptCard>
      <!-- Category filter chips -->
      <div class="filter-chips">
        {#each categories() as cat}
          <button
            class="filter-chip"
            class:filter-chip--active={selectedCategory === cat}
            onclick={() => selectedCategory = cat}
          >
            {cat}
          </button>
        {/each}
      </div>

      <ReceiptDivider variant="dashed" spacing="sm" />

      <!-- Pending Items -->
      {#if pendingItems.length > 0}
        <ReceiptLabel text="PENDING ITEMS ({pendingCount})" />
        {#each pendingItems as item}
          <div class="check-item">
            <span class="check-box">[ ]</span>
            <div class="check-content">
              <ReceiptItemRow
                name={item.name}
                amount={Math.round(item.price_cents * item.quantity / 100)}
                quantity={item.quantity}
                category={item.category}
              />
            </div>
          </div>
        {/each}
      {:else}
        <ReceiptLabel text="PENDING ITEMS" />
        <div class="empty-message">모든 항목을 구매했습니다!</div>
      {/if}

      <ReceiptDivider variant="double" />

      <!-- Summary -->
      <ReceiptSummary
        items={[
          { label: `예상 총액 (${pendingCount}개)`, amount: Math.round(totalPendingCents / 100) },
          { label: `구매 완료 (${purchasedCount}개)`, amount: Math.round(totalPurchasedCents / 100), highlight: true },
        ]}
        total={{ label: '남은 금액', amount: Math.round(totalPendingCents / 100) }}
      />

      <!-- Purchased Items -->
      {#if purchasedItems.length > 0}
        <ReceiptDivider variant="dashed" />
        <ReceiptLabel text="PURCHASED ({purchasedCount})" />
        {#each purchasedItems as item}
          <div class="purchased-item">
            <span class="check-box check-box--done">[X]</span>
            <div class="check-content">
              <ReceiptItemRow
                name={item.name}
                amount={Math.round(item.price_cents * item.quantity / 100)}
                quantity={item.quantity}
                category={item.category}
                excluded={true}
                note={item.purchased_at ? `구매: ${item.purchased_at}` : ''}
              />
            </div>
          </div>
        {/each}
      {/if}

      <ReceiptFooter
        lines={[
          `TRIP: ${trip.name}`,
          `ITEMS: ${items.length} (PENDING: ${pendingItems.length} / DONE: ${purchasedItems.length})`,
        ]}
        timestamp={timestamp}
      />
    </ReceiptCard>
  {/snippet}
</ReceiptPageLayout>

<style>
  .filter-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 4px;
  }

  .filter-chip {
    font-family: var(--font-mono);
    font-size: 10px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: var(--receipt-text-secondary);
    background: none;
    border: 1px dashed var(--receipt-divider);
    border-radius: 2px;
    padding: 4px 10px;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
  }

  .filter-chip:hover {
    border-color: var(--receipt-text-accent, #20B2AA);
    color: var(--receipt-text-accent, #20B2AA);
  }

  .filter-chip--active {
    background: var(--receipt-text-accent, #20B2AA);
    color: var(--receipt-bg, #1a1a1a);
    border-color: var(--receipt-text-accent, #20B2AA);
    border-style: solid;
    font-weight: 700;
  }

  .check-item,
  .purchased-item {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    width: 100%;
  }

  .check-box {
    font-family: var(--font-mono);
    font-size: var(--receipt-size-body, 13px);
    color: var(--receipt-text-muted);
    flex-shrink: 0;
    line-height: 1.6;
    user-select: none;
    margin-top: 0;
  }

  .check-box--done {
    color: var(--receipt-text-accent, #20B2AA);
  }

  .check-content {
    flex: 1;
    min-width: 0;
  }

  .purchased-item {
    opacity: 0.55;
  }

  .empty-message {
    font-family: var(--font-mono);
    font-size: var(--receipt-size-body, 13px);
    color: var(--receipt-text-accent, #20B2AA);
    text-align: center;
    padding: 16px 0;
    letter-spacing: 0.5px;
  }
</style>
